<script setup lang="ts">
import ChartContent from '@/components/Every24HoursReportPreviewParts/ChartContent.vue';
import {
  createMinMaxScaleLabel,
  xLabels,
  createScaleMin,
  getVerticalLines,
  formatDisplayChart,
  formatDisplayDistanceChart,
  getDistanceAtOutputTime,
  getDrivingTime,
  getDisplayData
} from '@/services/functions/every24HoursChart';
import every24HoursPreviewType from "@/types/responses/every24HoursPreview";
import {
  SPEED_OPTIONS,
  SPEED_CHART_LABELS,
  DISTANCE_OPTIONS,
  DISTANCE_CHART_LABELS,
  ROTATION_SPEED_OPTIONS,
  ROTATION_SPEED_CHART_LABELS,
} from "@/services/consts/every_24_hours_report";
import { Ref, ref, onMounted, watch } from 'vue';

const emits = defineEmits(['changeDetailData']);

const slideBarValue: Ref<number> = ref(0);
const speedData: Ref<number[][]> = ref([]);
const displaySpeedData: Ref<number[][]> = ref([]);
const distanceData: Ref<number[][]> = ref([]);
const displayDistanceData: Ref<number[][]> = ref([]);
const rotationSpeedData: Ref<number[][]> = ref([]);
const displayRotationSpeedData: Ref<number[][]> = ref([]);
const scaleMinMax: Ref<number[]> = ref([0, 0]);
const verticalLines: Ref<{ xAxis: number | null }[]> = ref([]);
const xLabel: Ref<string[]> = ref([]);

const props = defineProps<{
  index: number
  deliveryPlan: every24HoursPreviewType
  outPutTime: string
}>()

onMounted(() => {
  makeData()
})

async function makeData() {
  await Promise.all([
    speedData.value = formatDisplayChart(props.deliveryPlan.speeds),
    distanceData.value = formatDisplayDistanceChart(props.deliveryPlan.distances),
    rotationSpeedData.value = formatDisplayChart(props.deliveryPlan.rotation_speeds)
  ]);
  changeMinMax();
  emitChartDetail();
}

async function changeMinMax() {
  scaleMinMax.value = getMinMax();
  displaySpeedData.value = getDisplayData(speedData.value, scaleMinMax.value);
  displayDistanceData.value = getDisplayData(distanceData.value, scaleMinMax.value);
  displayRotationSpeedData.value = getDisplayData(rotationSpeedData.value, scaleMinMax.value)
  xLabel.value = xLabels(scaleMinMax.value, props.outPutTime);

  setVerticalLines();
  emitChartDetail();
}

function emitChartDetail() {
  emits('changeDetailData', {
    index: props.index,
    displayStartTime: scaleMinMax.value[0],
    drivingDistance: getDistanceAtOutputTime(distanceData.value, scaleMinMax.value),
    drivingTime: getDrivingTime(props.deliveryPlan, scaleMinMax.value),
    speed: displaySpeedData.value,
    distance: displayDistanceData.value,
    rotationSpeed: displayRotationSpeedData.value,
    xLabel: xLabel.value
  });
}

function getMinMax() {
  const minScale = createScaleMin(props.deliveryPlan, slideBarValue.value);
  const minMaxScale = createMinMaxScaleLabel(minScale, props.outPutTime);
  return minMaxScale;
}

function setVerticalLines() {
  const lines = getVerticalLines(scaleMinMax.value, props.outPutTime);
  return verticalLines.value = lines;
}

watch(() => props.outPutTime, () => {
  slideBarValue.value = 0;
  changeMinMax();
}, { immediate: true });
</script>
<template>
  <div class="chart-area w-100 mx-auto mt-3">
    <ChartContent
      ref="chartContent"
      type="speed"
      v-model="slideBarValue"
      :scaleMinMax="scaleMinMax"
      :chartData="speedData"
      :displayData="displaySpeedData"
      :xLabels="xLabel"
      :verticalLines="verticalLines"
      :options="SPEED_OPTIONS"
      :chartLabels="SPEED_CHART_LABELS"
      chartHeight="220"
      :changeMinMax="changeMinMax"
    />
    <ChartContent
      ref="chartContent"
      type="distance"
      v-model="slideBarValue"
      :scaleMinMax="scaleMinMax"
      :chartData="distanceData"
      :displayData="displayDistanceData"
      :xLabels="xLabel"
      :verticalLines="verticalLines"
      :options="DISTANCE_OPTIONS"
      :chartLabels="DISTANCE_CHART_LABELS"
      chartHeight="235"
      :changeMinMax="changeMinMax"
    />
    <ChartContent
      ref="chartContent"
      type="rotationSpeed"
      v-model="slideBarValue"
      :scaleMinMax="scaleMinMax"
      :chartData="rotationSpeedData"
      :displayData="displayRotationSpeedData"
      :xLabels="xLabel"
      :verticalLines="verticalLines"
      :options="ROTATION_SPEED_OPTIONS"
      :chartLabels="ROTATION_SPEED_CHART_LABELS"
      chartHeight="140"
      :changeMinMax="changeMinMax"
    />
  </div>
</template>
